import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { FundAtom, SelectedFundAtom, SidebarStateAtom } from "../../../store";
import { Fund } from "../../../types/Fund";
import { getFunds } from "@/api";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";

interface UseFundSelectorController {
  isSidebarCollapsed: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredFunds: Fund[];
  selectFund: (fund: Fund) => void;
  selectedFund?: Fund;
  refresh: () => void;
}

export const useFundSelectorController = (): UseFundSelectorController => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFund, setSelectedFund] = useAtom(SelectedFundAtom);
  const setFunds = useSetAtom(FundAtom);
  const setSelectedReportingPeriodAtom = useSetAtom(
    SelectedReportingPeriodAtom
  );
  const isCollapsed = useAtomValue(SidebarStateAtom);

  const result = useQuery({
    queryKey: ["funds"],
    queryFn: async () => {
      const funds = await getFunds();

      setFunds(funds);

      return funds;
    },
  });

  const refresh = () => {
    result.refetch();
  };

  useEffect(() => {
    if (open) {
      refresh();
    }
  }, [open]);

  const selectFund = (fund: Fund) => {
    console.log("selectFund", fund);
    setSelectedFund(fund);
    setSelectedReportingPeriodAtom(undefined);

    setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  const filteredFunds = useMemo(() => {
    if (result.data === undefined) return [];

    if ((searchQuery ?? "").trim() !== "") {
      return result.data.filter((fund) =>
        fund.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return result.data ?? [];
  }, [searchQuery, result.data]);

  return {
    isSidebarCollapsed: isCollapsed,
    open,
    setOpen,
    searchQuery,
    setSearchQuery,
    filteredFunds,
    selectFund,
    selectedFund: selectedFund,
    refresh,
  };
};
